import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { getEventRecordingsApi, addEventRecordingApi, deleteEventRecordingApi } from '../../../api/events';

export const EventRecordingsModal = ({ show, onHide, eventId, communityId, isOwner }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    if (show && eventId) {
      fetchRecordings();
    }
  }, [show, eventId]);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      const res = await getEventRecordingsApi({ eventId });
      setRecordings(res.data || []);
    } catch (err) {
      console.warn('Failed to fetch event recordings', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recording?')) return;
    try {
      await deleteEventRecordingApi(id);
      fetchRecordings();
    } catch (err) {
      alert('Failed to delete recording.');
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.url) {
      alert('Please provide title and video link');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('community', communityId);
      fd.append('event', eventId);
      fd.append('title', formData.title);
      fd.append('recording_link', formData.url); // Use URL instead of file
      await addEventRecordingApi(fd);
      setShowAddForm(false);
      setFormData({ title: '', url: '' });
      fetchRecordings();
    } catch (err) {
      alert('Failed to add recording.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold">Class Recordings</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {showAddForm ? (
          <div className="bg-light p-3 rounded-4 mb-3 border">
            <h6 className="fw-bold mb-3">Add New Recording</h6>
            <Form onSubmit={handleAddSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="Recording title" 
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required 
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Video Link</Form.Label>
                <Form.Control 
                  type="url" 
                  placeholder="Paste YouTube or Vimeo link here"
                  value={formData.url}
                  onChange={e => setFormData({ ...formData, url: e.target.value })}
                  required 
                />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="light" className="rounded-pill" onClick={() => setShowAddForm(false)}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={submitting} className="rounded-pill px-4 cd-btn-orange border-0">
                  {submitting ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </Form>
          </div>
        ) : (
          <>
            {isOwner && (
              <div className="d-flex justify-content-end mb-3">
                <Button variant="primary" className="rounded-pill px-4 cd-btn-orange border-0" onClick={() => setShowAddForm(true)}>
                  <i className="bi bi-plus-lg me-1"></i> Add Recording
                </Button>
              </div>
            )}
            
            {playingVideo ? (
              <div className="bg-light p-3 rounded-4 border">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center">
                    <button className="btn btn-link text-dark p-0 me-3" onClick={() => setPlayingVideo(null)}>
                      <i className="bi bi-chevron-left fs-5"></i>
                    </button>
                    <h6 className="fw-bold mb-0 text-truncate">{playingVideo.title}</h6>
                  </div>
                </div>
                <div className="ratio ratio-16x9 rounded-3 overflow-hidden bg-dark">
                  {(playingVideo.url.includes('youtube.com') || playingVideo.url.includes('vimeo.com') || playingVideo.url.includes('youtu.be')) ? (
                    <iframe src={playingVideo.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} allowFullScreen title="Video"></iframe>
                  ) : (
                    <video controls src={playingVideo.url} className="w-100 h-100 object-fit-cover"></video>
                  )}
                </div>
              </div>
            ) : (
              <>
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : recordings.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-camera-video text-muted" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3">No Recordings Yet</h5>
                    <p className="text-muted">No recordings have been added for this class.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {recordings.map((rec) => (
                      <Col md={6} key={rec._id}>
                        <div 
                          className="card h-100 border shadow-sm rounded-4 position-relative"
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                          onClick={() => {
                            const url = rec.recording_link || rec.url || rec.video || rec.media;
                            if (url) {
                              setPlayingVideo({ title: rec.title, url });
                            } else {
                              alert('Video URL not available.');
                            }
                          }}
                          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          {isOwner && (
                            <button 
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(rec._id);
                              }}
                              title="Delete Recording"
                              style={{ zIndex: 2 }}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                          <div className="card-body text-center p-4 d-flex flex-column align-items-center justify-content-center">
                            <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm" style={{ width: '50px', height: '50px' }}>
                              <i className="bi bi-play-fill fs-3" style={{ marginLeft: '4px' }}></i>
                            </div>
                            <h6 className="mb-1 fw-bold text-dark">{rec.title}</h6>
                            <small className="text-muted">{rec.createdAt ? new Date(rec.createdAt).toLocaleDateString() : 'No date'}</small>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
